import { useState, useMemo } from 'react';
import { useSearch } from 'wouter';
import {
  AlertCircle,
  DollarSign,
  Plus,
  ChevronDown,
  ChevronUp,
  Search,
  Activity,
  Github,
  Users,
  UserCheck,
  UserX,
  Mail,
  Clock,
  X,
  RefreshCw,
  ChevronRight,
  Trash2,
} from 'lucide-react';
import {
  useGetUsers,
  usePatchUserRole,
  useGetRates,
  useGetRateHistory,
  useCreateRate,
  axiosInstance,
  customInstance,
} from '@edison/api-client-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth-store.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';

// ── Types ─────────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: 'tenant_admin', label: 'Admin' },
  { value: 'r_and_d_manager', label: 'R&D Manager' },
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'developer', label: 'Developer' },
] as const;

const ROLE_BADGE_VARIANT: Record<string, 'positive' | 'info' | 'default' | 'warning'> = {
  tenant_admin: 'positive',
  r_and_d_manager: 'info',
  member: 'default',
  viewer: 'default',
  developer: 'default',
};

type Tab = 'team' | 'rate-card';
type StatusFilter = 'active' | 'deactivated' | 'pending' | 'all';
type RoleFilter = 'all' | 'tenant_admin' | 'r_and_d_manager' | 'member' | 'viewer' | 'developer';

interface AuditEntry {
  id: string;
  timestamp: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  ipAddress: string | null;
}

interface UserExt {
  id: string;
  email: string;
  name?: string | null;
  role: string;
  active?: boolean;
  pendingInvitation?: boolean;
  clerkId?: string;
  githubUsername?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getAuthProvider(clerkId?: string): string {
  if (!clerkId) return '—';
  if (clerkId.startsWith('pending:')) return 'Pending invite';
  return 'Google SSO';
}

function getUserStatus(u: UserExt): 'active' | 'deactivated' | 'pending' {
  if (u.pendingInvitation) return 'pending';
  if (u.active === false) return 'deactivated';
  return 'active';
}

function getInitials(name?: string | null, email?: string): string {
  if (name) {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '');
  }
  return (email?.[0] ?? '?').toUpperCase();
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return formatDate(iso);
}

function actionLabel(action: string): string {
  const map: Record<string, string> = {
    'artefact.create': 'Created artefact',
    'artefact.update': 'Updated artefact',
    'artefact.delete': 'Deleted artefact',
    'project.create': 'Created project',
    'project.update': 'Updated project',
    'tfa.create': 'Created TFA panel',
    'tfa.advance': 'Advanced TFA stage',
    'tfa.lock': 'Locked TFA',
    'activity.create': 'Created activity',
    'activity.update': 'Updated activity',
    'time_entry.approve': 'Approved time entry',
    'time_entry.reject': 'Rejected time entry',
    'claim.create': 'Created claim',
    'claim.lock': 'Locked claim',
    'user.role_change': 'Changed user role',
    'user.invite': 'Invited user',
    'user.deactivate': 'Deactivated user',
    'user.reactivate': 'Reactivated user',
    'auth.login': 'Signed in',
    'signal.create': 'Added signal source',
  };
  return map[action] ?? action.replace(/\./g, ' ').replace(/_/g, ' ');
}

// ── Avatar ─────────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-violet-100 text-violet-700',
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

function Avatar({ user, size = 'md' }: { user: UserExt; size?: 'sm' | 'md' }) {
  const initials = getInitials(user.name, user.email);
  const colorIdx = user.id.charCodeAt(0) % AVATAR_COLORS.length;
  const color = AVATAR_COLORS[colorIdx] ?? AVATAR_COLORS[0]!;
  const sizeClass = size === 'sm' ? 'h-7 w-7 text-xs' : 'h-9 w-9 text-sm';
  return (
    <div
      className={`${sizeClass} ${color} rounded-full flex items-center justify-center font-semibold shrink-0 select-none`}
      aria-hidden="true"
    >
      {initials.toUpperCase()}
    </div>
  );
}

// ── Summary stat chip ──────────────────────────────────────────────────────────

function StatChip({
  icon: Icon,
  label,
  value,
  active,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition-all text-left ${
        active
          ? 'border-primary/40 bg-primary/5 text-primary'
          : 'border-border bg-card hover:border-primary/20 hover:bg-muted/40 text-foreground'
      }`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
      <div>
        <p className={`text-xl font-semibold font-mono tabular-nums leading-none ${active ? 'text-primary' : ''}`}>
          {value}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      </div>
    </button>
  );
}

// ── Activity panel ─────────────────────────────────────────────────────────────

function UserActivityPanel({ user, onClose }: { user: UserExt; onClose: () => void }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['user-activity', user.id],
    queryFn: () =>
      customInstance<{ data: { items: AuditEntry[] } }>({
        url: `/api/audit?userId=${user.id}&limit=50`,
        method: 'GET',
      }).then((r) => r.data.items),
    staleTime: 30_000,
  });

  return (
    <SlidePanel open onClose={onClose} title={`Activity — ${user.name ?? user.email}`} width="w-[480px]">
      {/* User summary header */}
      <div className="px-5 py-4 border-b bg-muted/30 flex items-center gap-3">
        <Avatar user={user} size="md" />
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{user.name ?? user.email}</p>
          {user.name && <p className="text-xs text-muted-foreground truncate">{user.email}</p>}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant={ROLE_BADGE_VARIANT[user.role] ?? 'default'} className="text-xs">
              {ROLE_OPTIONS.find((r) => r.value === user.role)?.label ?? user.role}
            </Badge>
            {getAuthProvider(user.clerkId) !== '—' && (
              <span className="text-xs text-muted-foreground">{getAuthProvider(user.clerkId)}</span>
            )}
          </div>
        </div>
      </div>

      {/* Meta row */}
      <div className="px-5 py-3 border-b grid grid-cols-2 gap-3 text-xs">
        <div>
          <p className="text-muted-foreground uppercase tracking-wide text-[10px] font-medium mb-0.5">Member since</p>
          <p className="font-medium">{formatDate(user.createdAt)}</p>
        </div>
        <div>
          <p className="text-muted-foreground uppercase tracking-wide text-[10px] font-medium mb-0.5">GitHub</p>
          <p className="font-medium font-mono">{user.githubUsername ?? '—'}</p>
        </div>
      </div>

      {/* Activity list */}
      <div className="px-5 pt-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent activity</p>
          <button
            onClick={() => void refetch()}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Refresh activity"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex gap-3">
                <div className="h-3 w-3 mt-1 rounded-full skeleton shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-32 rounded skeleton" />
                  <div className="h-2.5 w-20 rounded skeleton" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Failed to load activity.</span>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="py-8 text-center">
            <Activity className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No recorded activity yet.</p>
          </div>
        ) : (
          <div className="space-y-0 relative">
            {/* Vertical timeline line */}
            <div className="absolute left-[5px] top-2 bottom-2 w-px bg-border" aria-hidden="true" />
            {data.map((entry) => (
              <div key={entry.id} className="flex gap-4 pb-4 relative">
                <div
                  className="h-2.5 w-2.5 rounded-full bg-border border-2 border-background shrink-0 mt-1.5 relative z-10"
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight">{actionLabel(entry.action)}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs text-muted-foreground">{formatRelativeTime(entry.timestamp)}</span>
                    {entry.entityType && (
                      <span className="text-xs text-muted-foreground/60 capitalize">
                        · {entry.entityType.replace(/_/g, ' ')}
                      </span>
                    )}
                    {entry.ipAddress && (
                      <span className="text-xs text-muted-foreground/40 font-mono">{entry.ipAddress}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SlidePanel>
  );
}

// ── Rate history ───────────────────────────────────────────────────────────────

function RateHistory({ userId, onDeleted }: { userId: string; onDeleted: () => void }) {
  const { data, isLoading, refetch } = useGetRateHistory(userId);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const history = data?.data?.items ?? [];

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await customInstance({ url: `/api/rates/${id}`, method: 'DELETE' });
      await refetch();
      onDeleted();
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) return <div className="text-xs text-muted-foreground px-4 py-2 animate-pulse">Loading…</div>;
  if (history.length === 0) return <div className="text-xs text-muted-foreground px-4 py-2">No rates set.</div>;

  return (
    <div className="px-4 py-2 space-y-1">
      {history.map((r) => (
        <div key={r.id} className="flex items-center justify-between text-xs text-muted-foreground group">
          <span>Effective {formatDate(r.effectiveFrom)}</span>
          <div className="flex items-center gap-3">
            <span className="font-mono tabular-nums">${parseFloat(r.hourlyRate).toFixed(2)}/hr</span>
            <button
              onClick={() => void handleDelete(r.id)}
              disabled={deletingId === r.id}
              aria-label="Delete rate"
              className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Rate card tab ──────────────────────────────────────────────────────────────

type RowState = { rate: string; date: string; error: string | null };

function RateCardTab({ users }: { users: UserExt[] }) {
  const { data: ratesData, isLoading, error, refetch } = useGetRates();
  const { mutate: createRate, isPending: isCreating } = useCreateRate();

  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const rates = ratesData?.data?.items ?? [];
  const currentRateByUser = new Map<string, (typeof rates)[0]>();
  for (const r of rates) {
    if (!currentRateByUser.has(r.userId)) currentRateByUser.set(r.userId, r);
  }

  // Default effectiveFrom to the start of the current Australian FY so rates
  // set today retroactively cover the whole year (e.g. 2025-07-01 for FY2026).
  function currentFyStart(): string {
    const now = new Date();
    const year = now.getFullYear();
    const fyEndYear = now.getMonth() >= 6 ? year : year - 1;
    return `${fyEndYear - 1}-07-01`;
  }

  function getRow(userId: string): RowState {
    return rowState[userId] ?? { rate: '', date: currentFyStart(), error: null };
  }

  function setRowField(userId: string, field: keyof RowState, value: string | null) {
    setRowState((prev) => ({
      ...prev,
      [userId]: { ...getRow(userId), [field]: value },
    }));
  }

  const handleAdd = (userId: string) => {
    const { rate, date } = getRow(userId);
    const parsed = parseFloat(rate);
    if (!parsed || parsed <= 0) return;
    createRate(
      { data: { userId, hourlyRate: parsed, effectiveFrom: date } },
      {
        onSuccess: () => {
          setAddingFor(null);
          setRowState((prev) => {
            const next = { ...prev };
            delete next[userId];
            return next;
          });
          void refetch();
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { error?: { message?: string } } } })
              ?.response?.data?.error?.message ?? 'Failed to save rate. The date may already have a rate set.';
          setRowField(userId, 'error', msg);
        },
      }
    );
  };

  const activeUsers = users.filter((u) => getUserStatus(u) !== 'deactivated');

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => <div key={i} className="h-12 w-full rounded-lg skeleton" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to load rate card.</span>
        <button onClick={() => refetch()} className="ml-auto underline text-xs">Retry</button>
      </div>
    );
  }

  const unrated = activeUsers.filter((u) => !currentRateByUser.has(u.id));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium">Hourly rates</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Used to calculate R&D labour expenditure on claims. Rate history is preserved — existing rates are never overwritten.
          </p>
        </div>
        {unrated.length > 0 && (
          <Badge variant="warning" className="shrink-0 ml-4">{unrated.length} without rate</Badge>
        )}
      </div>

      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Staff member</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Current rate</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Effective from</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {activeUsers.map((u) => {
              const current = currentRateByUser.get(u.id);
              const isExpanded = expandedId === u.id;
              const isAdding = addingFor === u.id;

              return (
                <>
                  <tr key={u.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar user={u} size="sm" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{u.name ?? u.email}</div>
                          {u.name && <div className="text-xs text-muted-foreground truncate">{u.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums">
                      {current
                        ? `$${parseFloat(current.hourlyRate).toFixed(2)}/hr`
                        : <span className="text-warning text-xs font-sans">No rate set</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {formatDate(current?.effectiveFrom)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => {
                            if (isAdding) {
                              setAddingFor(null);
                            } else {
                              // Always re-seed from current rate when opening the form
                              setRowState((prev) => ({
                                ...prev,
                                [u.id]: {
                                  rate: current ? parseFloat(current.hourlyRate).toFixed(2) : '',
                                  date: currentFyStart(),
                                  error: null,
                                },
                              }));
                              setAddingFor(u.id);
                            }
                          }}
                          className="text-xs text-primary underline hover:no-underline"
                          aria-label={`Set new rate for ${u.name ?? u.email}`}
                        >
                          {current ? 'Update rate' : 'Set rate'}
                        </button>
                        {current && (
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : u.id)}
                            className="text-xs text-muted-foreground hover:text-foreground"
                            aria-label={isExpanded ? 'Hide history' : 'Show history'}
                          >
                            {isExpanded
                              ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                              : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                            }
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isAdding && (
                    <tr key={`${u.id}-add`} className="border-b bg-muted/20">
                      <td colSpan={4} className="px-4 py-3">
                        <div className="flex items-end gap-3 flex-wrap">
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1" htmlFor={`rate-${u.id}`}>Hourly rate (AUD)</label>
                            <input
                              id={`rate-${u.id}`}
                              type="number" min={0} max={10000} step={5}
                              placeholder="e.g. 150"
                              value={getRow(u.id).rate}
                              onChange={(e) => setRowField(u.id, 'rate', e.target.value)}
                              className="w-28 rounded-lg border bg-background px-2 py-1 text-xs"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1" htmlFor={`date-${u.id}`}>Effective from</label>
                            <input
                              id={`date-${u.id}`}
                              type="date"
                              value={getRow(u.id).date}
                              onChange={(e) => setRowField(u.id, 'date', e.target.value)}
                              className="rounded-lg border bg-background px-2 py-1 text-xs"
                            />
                          </div>
                          <Button variant="primary" size="sm" onClick={() => handleAdd(u.id)} disabled={isCreating || !getRow(u.id).rate}>
                            {isCreating ? 'Saving…' : 'Save rate'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setAddingFor(null); setRowField(u.id, 'error', null); }}>Cancel</Button>
                        </div>
                        {getRow(u.id).error && (
                          <p className="text-xs text-destructive mt-2 flex items-center gap-1.5">
                            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                            {getRow(u.id).error}
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                  {isExpanded && (
                    <tr key={`${u.id}-history`} className="border-b bg-muted/10">
                      <td colSpan={4}><RateHistory userId={u.id} onDeleted={() => void refetch()} /></td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Rate history is preserved for audit purposes. Each rate applies from its effective date until the next rate's effective date.
      </p>
    </div>
  );
}

// ── User row ───────────────────────────────────────────────────────────────────

function UserRow({
  u,
  currentUserId,
  isAdmin,
  currentRate,
  onViewActivity,
  onRefetch,
}: {
  u: UserExt;
  currentUserId: string | undefined;
  isAdmin: boolean;
  currentRate?: string;
  onViewActivity: (u: UserExt) => void;
  onRefetch: () => void;
}) {
  const [editingRole, setEditingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState(u.role);
  const [actionLoading, setActionLoading] = useState(false);
  const { mutate: patchRole, isPending: isPatching } = usePatchUserRole();

  const status = getUserStatus(u);
  const isSelf = u.id === currentUserId;
  const authProvider = getAuthProvider(u.clerkId);

  const handleSaveRole = () => {
    patchRole(
      { id: u.id, data: { role: selectedRole as 'tenant_admin' | 'r_and_d_manager' | 'member' | 'viewer' | 'developer' } },
      { onSuccess: () => { setEditingRole(false); onRefetch(); } }
    );
  };

  const handleDeactivate = async () => {
    setActionLoading(true);
    try {
      await axiosInstance.patch(`/api/users/${u.id}/deactivate`);
      onRefetch();
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await axiosInstance.patch(`/api/users/${u.id}/reactivate`);
      onRefetch();
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <tr className="border-b last:border-0 hover:bg-muted/20 transition-colors">
      {/* User */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar user={u} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm truncate" title={u.name ?? u.email}>{u.name ?? u.email}</span>
              {isSelf && <span className="text-xs text-muted-foreground italic">(you)</span>}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Mail className="h-3 w-3 text-muted-foreground/50 shrink-0" aria-hidden="true" />
              <span className="text-xs text-muted-foreground truncate">{u.email}</span>
            </div>
            {u.githubUsername && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <Github className="h-3 w-3 text-muted-foreground/50 shrink-0" aria-hidden="true" />
                <span className="text-xs text-muted-foreground font-mono">{u.githubUsername}</span>
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Role */}
      <td className="px-4 py-3">
        {editingRole ? (
          <div className="flex items-center gap-2">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="rounded-lg border bg-background px-2 py-1 text-xs"
              aria-label="Select new role"
            >
              {ROLE_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <Button variant="primary" size="sm" onClick={handleSaveRole} disabled={isPatching}>
              {isPatching ? '…' : 'Save'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditingRole(false)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <Badge variant={ROLE_BADGE_VARIANT[u.role] ?? 'default'}>
            {ROLE_OPTIONS.find((r) => r.value === u.role)?.label ?? u.role}
          </Badge>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        {status === 'active' && <Badge variant="positive">Active</Badge>}
        {status === 'deactivated' && <Badge variant="default" className="text-muted-foreground">Deactivated</Badge>}
        {status === 'pending' && <Badge variant="warning">Invited</Badge>}
      </td>

      {/* Auth provider */}
      <td className="px-4 py-3">
        <span className="text-xs text-muted-foreground">{authProvider}</span>
      </td>

      {/* Rate */}
      <td className="px-4 py-3">
        <span className={`text-xs font-mono tabular-nums ${currentRate ? '' : 'text-muted-foreground'}`}>
          {currentRate ? `$${parseFloat(currentRate).toFixed(0)}/hr` : '—'}
        </span>
      </td>

      {/* Joined */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span>{formatDate(u.createdAt)}</span>
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onViewActivity(u)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors"
            title="View activity"
          >
            <Activity className="h-3.5 w-3.5" />
            <span className="sr-only">Activity</span>
          </button>

          {isAdmin && !isSelf && (
            <>
              {!editingRole && (
                <button
                  onClick={() => { setEditingRole(true); setSelectedRole(u.role); }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors"
                  title="Change role"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                  <span className="sr-only">Change role</span>
                </button>
              )}
              {status === 'active' && (
                <button
                  onClick={() => void handleDeactivate()}
                  disabled={actionLoading}
                  className="text-xs text-destructive hover:text-destructive/80 px-2 py-1 rounded-md hover:bg-destructive/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Deactivate"
                >
                  <UserX className="h-3.5 w-3.5" />
                  <span className="sr-only">Deactivate</span>
                </button>
              )}
              {status === 'deactivated' && (
                <button
                  onClick={() => void handleReactivate()}
                  disabled={actionLoading}
                  className="text-xs text-positive hover:text-positive/80 px-2 py-1 rounded-md hover:bg-positive/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Reactivate"
                >
                  <UserCheck className="h-3.5 w-3.5" />
                  <span className="sr-only">Reactivate</span>
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Invite form ────────────────────────────────────────────────────────────────

function InviteForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'r_and_d_manager' | 'viewer' | 'tenant_admin' | 'developer'>('member');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.post<{ data: { clerkInviteSent: boolean; clerkInviteError?: string | null } }>(
        '/api/users/invite', { email, role }
      );
      if (!res.data?.data?.clerkInviteSent) {
        // User created in Edison but Clerk email failed — still succeeds, but warn the admin
        const reason = res.data?.data?.clerkInviteError ?? 'unknown error';
        setError(`User added, but the invite email could not be sent (${reason}). Ask them to sign in directly at edison.polusailabs.com — they will be auto-provisioned on first login.`);
        // Don't call onDone — keep the form open so admin sees the warning
        return;
      }
      onDone();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Failed to send invite.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Invite a team member</p>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
      <form onSubmit={(e) => void handleSubmit(e)} className="flex items-end gap-3 flex-wrap">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="invite-email">
            Email address
          </label>
          <input
            id="invite-email"
            type="email"
            placeholder="colleague@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex h-9 w-64 rounded-lg border border-input bg-background px-3 py-1 text-sm"
            required
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="invite-role">Role</label>
          <select
            id="invite-role"
            className="flex h-9 rounded-lg border border-input bg-background px-3 py-1 text-sm"
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
          >
            <option value="developer">Developer</option>
            <option value="member">Member</option>
            <option value="viewer">Viewer</option>
            <option value="r_and_d_manager">R&D Manager</option>
          </select>
        </div>
        <Button type="submit" variant="primary" size="sm" disabled={!email || loading}>
          {loading ? 'Sending…' : 'Send invite'}
        </Button>
      </form>
      {error && (
        <div className="flex items-center gap-2 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function UserListPage() {
  const { user: currentUser } = useAuthStore();
  const { data, isLoading, error, refetch } = useGetUsers();
  const { data: ratesData } = useGetRates();

  const searchString = useSearch();
  const defaultTab = new URLSearchParams(searchString).get('tab') === 'rate-card' ? 'rate-card' : 'team';
  const [tab, setTab] = useState<Tab>(defaultTab);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [showInvite, setShowInvite] = useState(false);
  const [activityUser, setActivityUser] = useState<UserExt | null>(null);

  const isAdmin = currentUser?.role === 'tenant_admin';

  const allUsers = useMemo(
    () =>
      (data?.data?.items ?? []).filter(
        (u): u is UserExt & { id: string; email: string; role: string } =>
          typeof u.id === 'string' && typeof u.email === 'string' && typeof u.role === 'string'
      ) as UserExt[],
    [data]
  );

  // Summary counts
  const totalCount = allUsers.length;
  const activeCount = allUsers.filter((u) => getUserStatus(u) === 'active').length;
  const deactivatedCount = allUsers.filter((u) => getUserStatus(u) === 'deactivated').length;
  const pendingCount = allUsers.filter((u) => getUserStatus(u) === 'pending').length;

  // Filtered users
  const filteredUsers = useMemo(() => {
    return allUsers.filter((u) => {
      const status = getUserStatus(u);
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const nameMatch = u.name?.toLowerCase().includes(q) ?? false;
        const emailMatch = u.email.toLowerCase().includes(q);
        const githubMatch = u.githubUsername?.toLowerCase().includes(q) ?? false;
        if (!nameMatch && !emailMatch && !githubMatch) return false;
      }
      return true;
    });
  }, [allUsers, statusFilter, roleFilter, search]);

  // Current rate per user
  const rates = ratesData?.data?.items ?? [];
  const currentRateByUser = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rates) {
      if (!map.has(r.userId)) map.set(r.userId, r.hourlyRate);
    }
    return map;
  }, [rates]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-9 w-48 rounded-lg skeleton" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-xl skeleton" />)}
        </div>
        {[...Array(4)].map((_, i) => <div key={i} className="h-14 w-full rounded-lg skeleton" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to load users.</span>
        <button onClick={() => refetch()} className="ml-auto underline text-xs flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage team members, access, and hourly rates.
          </p>
        </div>
        {isAdmin && tab === 'team' && !showInvite && (
          <Button variant="primary" size="sm" onClick={() => setShowInvite(true)}>
            <Plus className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Invite user
          </Button>
        )}
      </div>

      {/* Summary stat chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip
          icon={Users}
          label="Total"
          value={totalCount}
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
        />
        <StatChip
          icon={UserCheck}
          label="Active"
          value={activeCount}
          active={statusFilter === 'active'}
          onClick={() => setStatusFilter('active')}
        />
        <StatChip
          icon={UserX}
          label="Deactivated"
          value={deactivatedCount}
          active={statusFilter === 'deactivated'}
          onClick={() => setStatusFilter('deactivated')}
        />
        <StatChip
          icon={Mail}
          label="Pending"
          value={pendingCount}
          active={statusFilter === 'pending'}
          onClick={() => setStatusFilter('pending')}
        />
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        {([
          { id: 'team' as Tab, label: 'Team members' },
          { id: 'rate-card' as Tab, label: 'Rate card', icon: DollarSign },
        ] as const).map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-primary text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Team tab */}
      {tab === 'team' && (
        <div className="space-y-4">
          {/* Invite form */}
          {showInvite && (
            <InviteForm
              onDone={() => { setShowInvite(false); void refetch(); }}
              onCancel={() => setShowInvite(false)}
            />
          )}

          {/* Search + filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search by name, email or GitHub…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                aria-label="Search users"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Role filter */}
            <div className="flex gap-1 flex-wrap">
              {([
                { value: 'all', label: 'All roles' },
                { value: 'tenant_admin', label: 'Admin' },
                { value: 'r_and_d_manager', label: 'R&D Manager' },
                { value: 'member', label: 'Member' },
                { value: 'viewer', label: 'Viewer' },
                { value: 'developer', label: 'Developer' },
              ] as const).map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setRoleFilter(value)}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                    roleFilter === value
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* User table */}
          {filteredUsers.length === 0 ? (
            <div className="py-12 text-center rounded-xl border border-dashed">
              <Users className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" aria-hidden="true" />
              <p className="font-medium text-sm">No users match your filters</p>
              {(search || statusFilter !== 'active' || roleFilter !== 'all') && (
                <button
                  onClick={() => { setSearch(''); setStatusFilter('active'); setRoleFilter('all'); }}
                  className="text-xs text-primary underline mt-2 hover:no-underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="rounded-xl border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Auth</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Joined</th>
                    <th className="px-4 py-3 w-24" />
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      currentUserId={currentUser?.id}
                      isAdmin={isAdmin}
                      currentRate={currentRateByUser.get(u.id)}
                      onViewActivity={setActivityUser}
                      onRefetch={() => void refetch()}
                    />
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 border-t bg-muted/20 text-xs text-muted-foreground">
                Showing {filteredUsers.length} of {totalCount} user{totalCount !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rate card tab */}
      {tab === 'rate-card' && (
        !isAdmin ? (
          <p className="text-sm text-muted-foreground">Rate card management requires admin access.</p>
        ) : (
          <RateCardTab users={allUsers} />
        )
      )}

      {/* Activity panel */}
      {activityUser && (
        <UserActivityPanel
          user={activityUser}
          onClose={() => setActivityUser(null)}
        />
      )}
    </div>
  );
}
